import React, { useState } from 'react';
import {
  Package,
  Globe,
  Truck,
  TrendingUp,
  Shield,
  Building,
  Plane,
  MapPin,
  CheckCircle,
  ArrowRight,
  FileText,
  UserCheck,
  Clock,
  Star,
  Phone,
  Mail,
  Users,
  Target
} from 'lucide-react';
import ResponsiveNavbar from './ResponsiveNavbar';

const ServicesPage: React.FC = () => {
  const [activeService, setActiveService] = useState(0);

  const services: any = [
    {
      id: 'express-document',
      icon: <Package className="w-8 h-8" />,
      title: 'Express Document Delivery',
      subtitle: 'Fast, Secure Document Services',
      description: 'Time-sensitive deliveries handled with care and urgency. Same-day and next-day delivery with real-time tracking.',
      image: 'https://images.unsplash.com/photo-1586880244406-556ebe35f282?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Same-Day Delivery',
          description: 'Urgent document delivery within Lagos and Abuja with flexible pickup and delivery windows.',
          icon: <Clock className="w-6 h-6" />
        },
        {
          title: 'Real-Time Tracking',
          description: 'Live tracking updates and notifications so you know exactly where your documents are at all times.',
          icon: <MapPin className="w-6 h-6" />
        },
        {
          title: 'Secure Handling',
          description: 'Signature confirmation and secure handling protocols to ensure your important documents are protected.',
          icon: <Shield className="w-6 h-6" />
        }
      ],
      idealFor: [
        'Legal documents and contracts',
        'Medical records and reports',
        'Important correspondence',
        'Business agreements',
        'Time-sensitive proposals'
      ]
    },
    {
      id: 'parcel-package',
      icon: <Package className="w-8 h-8" />,
      title: 'Parcel & Package Services',
      subtitle: 'Door-to-Door Package Delivery',
      description: 'Comprehensive parcel delivery services with volume discounts and insurance coverage included.',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Door-to-Door Service',
          description: 'Complete pickup and delivery service from your location to the recipient with professional handling.',
          icon: <Truck className="w-6 h-6" />
        },
        {
          title: 'Volume Discounts',
          description: 'Special pricing for businesses and individuals with regular shipping needs and bulk deliveries.',
          icon: <TrendingUp className="w-6 h-6" />
        },
        {
          title: 'Insurance Coverage',
          description: 'Comprehensive Goods in Transit (GIT) insurance protection for your valuable packages.',
          icon: <Shield className="w-6 h-6" />
        }
      ],
      coverage: [
        'All 36 Nigerian states',
        'Major cities and towns',
        'Remote locations nationwide',
        'E-commerce fulfillment',
        'Personal items and gifts',
        'Business supplies'
      ]
    },
    {
      id: 'haulage-cargo',
      icon: <Truck className="w-8 h-8" />,
      title: 'Haulage & Heavy Cargo',
      subtitle: 'Nationwide Truck Fleet Services',
      description: 'Professional haulage solutions for any size cargo with complete truck fleet and expert handling.',
      image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Complete Fleet',
          description: 'Modern truck fleet capable of handling all cargo sizes from small loads to oversized shipments.',
          icon: <Truck className="w-6 h-6" />
        },
        {
          title: 'Professional Loading',
          description: 'Expert loading, securing, and offloading services with special handling for fragile items.',
          icon: <Users className="w-6 h-6" />
        },
        {
          title: 'Route Optimization',
          description: 'Smart routing technology for fastest delivery times and dedicated account management.',
          icon: <Target className="w-6 h-6" />
        }
      ],
      capabilities: [
        'Heavy machinery transportation',
        'Construction materials',
        'Industrial equipment',
        'Oversized cargo handling',
        'Cross-country deliveries',
        'Special handling requirements'
      ]
    },
    {
      id: 'international-shipping',
      icon: <Plane className="w-8 h-8" />,
      title: 'International Shipping',
      subtitle: 'Global Network via Skynet',
      description: 'Connect to the world through our Skynet network with air freight, sea freight, and customs clearance.',
      image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Air Freight Services',
          description: 'Fast international shipping to all continents with express delivery options for urgent shipments.',
          icon: <Plane className="w-6 h-6" />
        },
        {
          title: 'Sea Freight Solutions',
          description: 'Cost-effective ocean freight for large shipments with Full Container Load (FCL) and Less than Container Load (LCL).',
          icon: <Globe className="w-6 h-6" />
        },
        {
          title: 'Customs Clearance',
          description: 'Expert customs documentation and clearance services to ensure smooth international deliveries.',
          icon: <FileText className="w-6 h-6" />
        }
      ],
      services: [
        'Door-to-door international delivery',
        'Import and export facilitation',
        'Duty and tax calculation',
        'Regulatory compliance',
        'Documentation preparation',
        'Tracking across borders'
      ]
    },
    {
      id: 'customs-clearing',
      icon: <FileText className="w-8 h-8" />,
      title: 'Customs Clearing Services',
      subtitle: 'Expert Customs Documentation',
      description: 'Navigate complex customs procedures with our experienced team and fast-track clearance options.',
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Air Cargo Clearance',
          description: 'Fast customs clearance for air freight shipments with expedited processing and documentation.',
          icon: <Plane className="w-6 h-6" />
        },
        {
          title: 'Sea Freight Clearance',
          description: 'Complete customs clearance for ocean freight with container handling and port coordination.',
          icon: <Globe className="w-6 h-6" />
        },
        {
          title: 'Documentation Support',
          description: 'Preparation of all required customs documents including invoices, permits, and compliance certificates.',
          icon: <FileText className="w-6 h-6" />
        }
      ],
      expertise: [
        'Nigerian customs regulations',
        'Import/export procedures',
        'Duty optimization strategies',
        'Compliance management',
        'Fast-track processing',
        'Appeals and disputes'
      ]
    },
    {
      id: 'specialized-services',
      icon: <Shield className="w-8 h-8" />,
      title: 'Specialized Services',
      subtitle: 'Tailored Logistics Solutions',
      description: 'Unique logistics solutions including repair & return services and comprehensive insurance options.',
      image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: [
        {
          title: 'Repair & Return Services',
          description: 'Equipment pickup for repairs, secure transportation to service centers, and return delivery after completion.',
          icon: <UserCheck className="w-6 h-6" />
        },
        {
          title: 'GIT Insurance',
          description: 'Comprehensive Goods in Transit insurance with multiple coverage options and claims processing support.',
          icon: <Shield className="w-6 h-6" />
        },
        {
          title: 'Risk Management',
          description: 'Professional risk assessment and management consulting for high-value or sensitive shipments.',
          icon: <Target className="w-6 h-6" />
        }
      ],
      solutions: [
        'Temperature-controlled shipping',
        'High-value cargo protection',
        'Technology equipment handling',
        'Medical device transportation',
        'Artwork and antiques',
        'Hazardous materials'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-green-50">
      {/* Hero Section */}
      <ResponsiveNavbar />
      <section className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4 mr-2" />
            Complete Logistics Solutions
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Our Expert
            <span className="bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent"> Services</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            From urgent documents to heavy cargo, we move it all with precision and care across Nigeria and worldwide.
          </p>
        </div>
      </section>

      {/* Services Navigation */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {services.map((service: any, index: any) => (
              <button
                key={index}
                onClick={() => setActiveService(index)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-sm lg:text-base ${activeService === index
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-red-50 hover:text-red-600 shadow-sm'
                  }`}
              >
                {service.icon}
                <span className="hidden sm:inline">{service.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active Service Detail */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            {/* Service Header with Image */}
            <div className="relative">
              <div className="h-64 sm:h-80 lg:h-96 overflow-hidden">
                <img
                  src={services[activeService].image}
                  alt={services[activeService].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/90 to-red-700/90"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white p-8">
                  <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    {services[activeService].icon}
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">{services[activeService].title}</h2>
                  <p className="text-red-100 text-lg sm:text-xl mb-4">{services[activeService].subtitle}</p>
                  <p className="text-red-50 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
                    {services[activeService].description}
                  </p>
                </div>
              </div>
            </div>

            {/* Service Features */}
            <div className="p-8 sm:p-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Key Features</h3>
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {services[activeService].features.map((feature: any, index: any) => (
                  <div key={index} className="group">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>

              {/* Additional Service Info */}
              <div className="grid md:grid-cols-2 gap-8">
                {services[activeService].idealFor && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Target className="w-5 h-5 mr-2 text-red-600" />
                      Ideal For
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].idealFor.map((item: any, index: any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].coverage && (
                  <div className="bg-red-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <MapPin className="w-5 h-5 mr-2 text-red-600" />
                      Coverage Areas
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].coverage.map((area: any, index: any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {area}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].capabilities && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Truck className="w-5 h-5 mr-2 text-red-600" />
                      Capabilities
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].capabilities.map((cap: any, index: any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {cap}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].services && (
                  <div className="bg-red-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Globe className="w-5 h-5 mr-2 text-red-600" />
                      Services Included
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].services.map((service: any, index: any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].expertise && (
                  <div className="bg-gray-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-red-600" />
                      Our Expertise
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].expertise.map((exp: any, index: any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <Star className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                          {exp}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {services[activeService].solutions && (
                  <div className="bg-red-50 rounded-2xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="w-5 h-5 mr-2 text-red-600" />
                      Specialized Solutions
                    </h4>
                    <ul className="space-y-2">
                      {services[activeService].solutions.map((solution: any, index: any) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Services Overview */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Complete Logistics Solutions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive services for every logistics need across Nigeria and internationally
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Package className="w-8 h-8" />,
                title: 'Express Document Delivery',
                description: 'Same-day and next-day delivery with secure handling',
                features: ['Real-time tracking', 'Signature confirmation', 'Secure protocols'],
                image: 'https://images.unsplash.com/photo-1586880244406-556ebe35f282?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Package className="w-8 h-8" />,
                title: 'Parcel & Package Services',
                description: 'Door-to-door delivery with volume discounts',
                features: ['Insurance included', 'Volume pricing', 'Nationwide coverage'],
                image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Truck className="w-8 h-8" />,
                title: 'Haulage & Heavy Cargo',
                description: 'Professional handling for any size cargo',
                features: ['Complete fleet', 'Expert loading', 'Route optimization'],
                image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Plane className="w-8 h-8" />,
                title: 'International Shipping',
                description: 'Global network via Skynet Express',
                features: ['Air & sea freight', 'Customs clearance', 'Door-to-door'],
                image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <FileText className="w-8 h-8" />,
                title: 'Customs Clearing Services',
                description: 'Expert customs documentation and clearance',
                features: ['Fast processing', 'Compliance', 'Documentation'],
                image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              },
              {
                icon: <Shield className="w-8 h-8" />,
                title: 'Specialized Services',
                description: 'Repair & return and insurance solutions',
                features: ['GIT insurance', 'Risk management', 'Custom solutions'],
                image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 group overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{service.description}</p>
                  <ul className="space-y-1">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-xs text-gray-500">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Ship with CFL Global?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Get an instant quote for your shipping needs or speak with our logistics experts about your requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href='/quote' className="bg-white text-red-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg flex items-center justify-center">
              <Package className="w-5 h-5 mr-2" />
              Get Instant Quote
            </a>
            <a href='/contact-us' className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-red-600 transition-all duration-300 flex items-center justify-center">
              <Phone className="w-5 h-5 mr-2" />
              Speak with Expert
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <a href='/' className="flex items-center space-x-2">
                  <img
                    src="logo.png"
                    alt="CFL Global Logistics"
                    className="w-50 h-12 rounded-xl object-contain"
                  />
                  <span className="text-xl font-bold text-white">CFL Global</span>
                </a>

              </div>
              <p className="text-gray-400 leading-relaxed">
                Your trusted logistics partner in Nigeria, connected to the world through the Skynet Worldwide Express network.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Express Document Delivery</li>
                <li>Parcel & Package Services</li>
                <li>Haulage & Heavy Cargo</li>
                <li>International Shipping</li>
                <li>Customs Clearing</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/tracking" className="hover:text-white transition-colors">Track Package</a></li>
                <li><a href="/quote" className="hover:text-white transition-colors">Get Quote</a></li>
                <li><a href="/about-us" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/contact-us" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <div>
                    <div>08023693696</div>
                    <div>08037921992</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <div className="text-sm">
                    <div>Mon-Fri: 8:00 AM - 6:00 PM</div>
                    <div>Sat: 9:00 AM - 4:00 PM</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 CFL Global Logistics Limited. All rights reserved. | A member of Skynet Worldwide Express</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;